import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import Logo from '../../Images/logo_main_black.webp';

let manualCount = 0;
let listeners: Array<() => void> = [];
const notify = () => listeners.forEach((fn) => fn());

export const globalLoaderStart = () => { manualCount++; notify(); };
export const globalLoaderEnd = () => { manualCount = Math.max(0, manualCount - 1); notify(); };

export const trackedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  globalLoaderStart();
  try {
    const res = await fetch(input, init);
    globalLoaderEnd();
    return res;
  } catch (e) {
    globalLoaderEnd();
    throw e;
  }
};

const SKIP_URLS = [
  '/email-hub/stats',
  '/email-hub/v3/notifications/unread-count',
  // Lightweight inline validation that runs while the user is typing —
  // must never raise the full-screen loading overlay.
  '/customer/check-duplicate-phone',
  // Background polling endpoints — these run on a timer (every 10-15s)
  // to detect new/changed leads without the operator's input, so they
  // must NEVER raise the full-screen overlay. Otherwise the page flashes
  // a loading spinner every interval and looks broken.
  '/leads/check-changes',
];

// RTK Query endpoint names whose pending state must NOT raise the global
// overlay. Same rationale as SKIP_URLS, for the Redux-store code path below.
const SKIP_ENDPOINTS = ['checkDuplicatePhone'];

const GlobalLoader: React.FC = () => {
  const [manualLoading, setManualLoading] = useState(false);
  const patchedRef = useRef(false);

  useEffect(() => {
    const handler = () => setManualLoading(manualCount > 0);
    listeners.push(handler);
    return () => { listeners = listeners.filter((fn) => fn !== handler); };
  }, []);

  useEffect(() => {
    if (patchedRef.current) return;
    patchedRef.current = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : (input as Request)?.url || '';
      const shouldSkip = SKIP_URLS.some((s) => url.includes(s));
      if (!shouldSkip) globalLoaderStart();
      return originalFetch(input, init)
        .then((res: Response) => { if (!shouldSkip) globalLoaderEnd(); return res; })
        .catch((err: any) => { if (!shouldSkip) globalLoaderEnd(); throw err; });
    }) as typeof fetch;

    const OrigXHROpen = XMLHttpRequest.prototype.open;
    const OrigXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL) {
      (this as any)._glSkip = SKIP_URLS.some((s) => (typeof url === 'string' ? url : url.toString()).includes(s));
      // eslint-disable-next-line prefer-rest-params
      return OrigXHROpen.apply(this, arguments as any);
    };

    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: any) {
      if (!(this as any)._glSkip) {
        globalLoaderStart();
        this.addEventListener('loadend', () => globalLoaderEnd(), { once: true });
      }
      return OrigXHRSend.call(this, body);
    };
  }, []);

  const queries = useSelector((state: any) => state.attoDeskApi?.queries ?? {});
  const mutations = useSelector((state: any) => state.attoDeskApi?.mutations ?? {});
  const hasPending = Object.values(queries).some(
      (q: any) => q?.status === 'pending' && !SKIP_ENDPOINTS.includes(q?.endpointName)
    )
    || Object.values(mutations).some((m: any) => m?.status === 'pending');

  const isLoading = hasPending || manualLoading;

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        pointerEvents: 'none',
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            position: 'absolute',
            width: 90,
            height: 90,
            border: '3px solid #e8e8e8',
            borderTop: '3px solid #c9a84c',
            borderRadius: '50%',
            animation: 'glspin 1.2s linear infinite',
          }}
        />
        <img src={Logo} alt="" style={{ width: 50, height: 'auto', animation: 'glfade 1.8s ease-in-out infinite' }} />
      </Box>
      <Box textAlign="center">
        <Box sx={{ color: '#1a3353', fontWeight: 700, letterSpacing: 1.5, fontSize: 11, textTransform: 'uppercase' }}>
          Durham Convention Centre
        </Box>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: '4px' }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 5, height: 5, borderRadius: '50%', bgcolor: '#c9a84c',
                animation: `gldot 1.2s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </Box>
      </Box>
      <style>
        {`
          @keyframes glspin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
          @keyframes glfade{0%,100%{opacity:1}50%{opacity:.5}}
          @keyframes gldot{0%,80%,100%{transform:scale(.6);opacity:.4}40%{transform:scale(1);opacity:1}}
        `}
      </style>
    </Box>
  );
};

export default GlobalLoader;
